import { useCallback, useMemo } from "react"
import { useLocalDateFilter } from "shared-ts/hooks/useFilter"
import {
  DisabledMode,
  FilterT,
  FilterType,
} from "shared-ts/hooks/useFilter/types/filter.types"
import {
  FullInterval,
  newFullIntervalFromTimestampRange,
} from "shared-ts/libs/dates/interval"
import {
  ALL_TIME_PRESET,
  listDateRangePresets,
} from "shared-ts/libs/dates/presets"
import { TimestampRange } from "shared-ts/libs/dates/timestampRange"

import { useUrlQuery } from "./useUrlQuery"

import type { UseDateFilterProps } from "shared-ts/hooks/useFilter"

export function useDateFilter({
  persistInUrl = true,
  key = "date",
  label = "Date",
  disabledMode = DisabledMode.Future,
  initialSelectedDateRange = null,
}: UseDateFilterProps = {}) {
  const localRes = useLocalDateFilter({
    key,
    label,
    disabledMode,
    initialSelectedDateRange,
  })
  const urlRes = useUrlDateFilter({ key, label, disabledMode })

  return useMemo(() => {
    return persistInUrl === false ? localRes : urlRes
  }, [persistInUrl, localRes, urlRes])
}

const presetsToDisplay = listDateRangePresets(false)

function useUrlDateFilter({
  key = "date",
  label = "Date",
  disabledMode = DisabledMode.Future,
}: Pick<UseDateFilterProps, "key" | "label" | "disabledMode">) {
  const { query, pushQuery } = useUrlQuery()
  const queryParam = query[key]

  const selectedDateRange = useMemo<FullInterval | null>(() => {
    if (!queryParam || typeof queryParam !== "string") {
      return null
    }

    const timestampRange = TimestampRange.fromQueryParam(queryParam)
    if (!timestampRange) return null

    return newFullIntervalFromTimestampRange(timestampRange)
  }, [queryParam])

  const onSelectDateRange = useCallback(
    (dateRange: FullInterval | null) => {
      const newQuery = { ...query }

      if (!dateRange) {
        delete newQuery[key]
      } else {
        const timestampRange = dateRange.toTimestampRangePB()
        const encoded = TimestampRange.toQueryParam(timestampRange)
        if (encoded === "") return
        newQuery[key] = encoded
      }

      pushQuery(newQuery)
    },
    [key, query, pushQuery]
  )

  const onClear = useCallback(() => {
    if (!queryParam) return
    const newQuery = { ...query }
    delete newQuery[key]
    pushQuery(newQuery)
  }, [query, queryParam, key, pushQuery])

  const filter: FilterT = {
    key,
    value: label,
    type: FilterType.Date,
    defaultPreset: ALL_TIME_PRESET,
    presetsToDisplay,
    selectedDateRange,
    onSelectDateRange,
    onClear,
    disabledMode,
  }

  return { filter, selectedDateRange }
}
